import type { TaskBoard, TaskBoardMeta } from '@shared/types/tasks';
import { loaderStore } from '@/hooks/loader-store';
import { apiData, type ApiResponse } from '@/lib/api-contract';
import { getTaskMeta } from '@/lib/tasks-api';

/**
 * A project's task meta (SF-T-172): its columns, labels, types, people and `#tags`, from
 * GET /api/tasks/meta rather than the whole Board. Cached per project in the loader store, and
 * remembered on this browser by the launch cache, so the `#` picker and the Inbox's task pop-up
 * show it at once; it loads again behind what's shown when used and older than
 * TASK_META_STALE_MS. Live changes keep it current without asking again where they can: a task change's
 * card refresh brings the tag counts and people (rememberLiveMeta), and a Board loading brings it all
 * (rememberTaskMeta); a board-wide change (columns, labels, types, a reconnect) reloads it while in use.
 */

/** How long the cached meta counts as fresh when something uses it. */
export const TASK_META_STALE_MS = 60_000;

const SUFFIX = ':tasks:meta';

export function taskMetaKey(projectId: string): string {
  return `${projectId}${SUFFIX}`;
}

export function isTaskMetaKey(key: string): boolean {
  return key.endsWith(SUFFIX) && key.length > SUFFIX.length;
}

/** A Board's answer carries the meta too: keep it, so what uses the meta needn't ask again. */
export function rememberTaskMeta(projectId: string, board: TaskBoard): void {
  const { columns, labels, types, members, tags, push_sources } = board;
  loaderStore.receive<ApiResponse<TaskBoardMeta>>(taskMetaKey(projectId), apiData({ columns, labels, types, members, tags, push_sources }));
}

/**
 * Tag counts and people from a live card refresh, into the cached meta (when there is one): what a task
 * change can alter, so the `#` picker keeps up with the Board without a request of its own.
 */
export function rememberLiveMeta(projectId: string, part: Pick<TaskBoardMeta, 'tags' | 'members'>): void {
  const key = taskMetaKey(projectId);
  const cached = loaderStore.snapshot<ApiResponse<TaskBoardMeta>>(key).data?.data;
  if (cached) loaderStore.receive<ApiResponse<TaskBoardMeta>>(key, apiData({ ...cached, tags: part.tags, members: part.members }));
}

/** Load a project's meta before something shows it (a task about to open, SF-T-236), unless it's fresh. */
export function prefetchTaskMeta(projectId: string): void {
  loaderStore.prefetch(taskMetaKey(projectId), (signal) => getTaskMeta({ project: projectId, signal }), { staleMs: TASK_META_STALE_MS });
}

/**
 * The meta once, outside a component (a menu opening New task in a project): the cached copy at once,
 * loaded again behind it when stale; with none cached, from the server, and kept.
 */
export async function loadTaskMeta(projectId: string): Promise<ApiResponse<TaskBoardMeta>> {
  const key = taskMetaKey(projectId);
  const cached = loaderStore.snapshot<ApiResponse<TaskBoardMeta>>(key);
  if (cached.hasData && cached.data?.data) {
    prefetchTaskMeta(projectId);
    return cached.data;
  }
  const res = await getTaskMeta({ project: projectId });
  if (res.data) loaderStore.receive(key, res);
  return res;
}
